/**
 * RichTextEditor
 * Rich text editor with formatting capabilities
 */

import { useRef, useCallback, useEffect, useState } from 'react'
import { clsx } from 'clsx'
import {
  Bold,
  Italic,
  Underline,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Undo,
  Redo,
  Image as ImageIcon,
  Code,
} from 'lucide-react'
import { Button } from '../ui'

export interface RichTextEditorProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  className?: string
  minHeight?: string
}

export function RichTextEditor({
  value,
  onChange,
  placeholder = 'Start writing...',
  className,
  minHeight = '500px',
}: RichTextEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null)
  const [isFocused, setIsFocused] = useState(false)

  // Initialize content
  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value
    }
  }, [value])

  const handleInput = useCallback(() => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML)
    }
  }, [onChange])

  const executeCommand = useCallback((command: string, value?: string) => {
    document.execCommand(command, false, value)
    editorRef.current?.focus()
    handleInput()
  }, [handleInput])

  const handleHeading = useCallback((level: number) => {
    executeCommand('formatBlock', `h${level}`)
  }, [executeCommand])

  const handleImageInsert = useCallback(() => {
    const url = prompt('Enter image URL:')
    if (url) {
      executeCommand('insertImage', url)
    }
  }, [executeCommand])

  const formatButton = (
    icon: React.ReactNode,
    command: string,
    title: string,
    value?: string
  ) => (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => executeCommand(command, value)}
      title={title}
      type="button"
      className="h-8 w-8 p-0"
    >
      {icon}
    </Button>
  )

  return (
    <div className={clsx('border border-gray-200 rounded-lg bg-white', className)}>
      {/* Toolbar */}
      <div className="border-b border-gray-200 p-2 flex flex-wrap gap-1">
        {/* Text Formatting */}
        <div className="flex gap-1 border-r border-gray-200 pr-2">
          {formatButton(<Bold className="h-4 w-4" />, 'bold', 'Bold (Ctrl+B)')}
          {formatButton(<Italic className="h-4 w-4" />, 'italic', 'Italic (Ctrl+I)')}
          {formatButton(<Underline className="h-4 w-4" />, 'underline', 'Underline (Ctrl+U)')}
        </div>

        {/* Headings */}
        <div className="flex gap-1 border-r border-gray-200 px-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleHeading(1)}
            title="Heading 1"
            type="button"
            className="h-8 w-8 p-0"
          >
            <Heading1 className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleHeading(2)}
            title="Heading 2"
            type="button"
            className="h-8 w-8 p-0"
          >
            <Heading2 className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleHeading(3)}
            title="Heading 3"
            type="button"
            className="h-8 w-8 p-0"
          >
            <Heading3 className="h-4 w-4" />
          </Button>
        </div>

        {/* Lists */}
        <div className="flex gap-1 border-r border-gray-200 px-2">
          {formatButton(<List className="h-4 w-4" />, 'insertUnorderedList', 'Bullet List')}
          {formatButton(<ListOrdered className="h-4 w-4" />, 'insertOrderedList', 'Numbered List')}
        </div>

        {/* Blocks */}
        <div className="flex gap-1 border-r border-gray-200 px-2">
          {formatButton(<Quote className="h-4 w-4" />, 'formatBlock', 'Block Quote', 'blockquote')}
          {formatButton(<Code className="h-4 w-4" />, 'formatBlock', 'Code Block', 'pre')}
        </div>

        {/* Image */}
        <div className="flex gap-1 border-r border-gray-200 px-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleImageInsert}
            title="Insert Image"
            type="button"
            className="h-8 w-8 p-0"
          >
            <ImageIcon className="h-4 w-4" />
          </Button>
        </div>

        {/* Undo/Redo */}
        <div className="flex gap-1 px-2">
          {formatButton(<Undo className="h-4 w-4" />, 'undo', 'Undo (Ctrl+Z)')}
          {formatButton(<Redo className="h-4 w-4" />, 'redo', 'Redo (Ctrl+Y)')}
        </div>
      </div>

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        className={clsx(
          'p-6 outline-none overflow-y-auto prose prose-sm sm:prose max-w-none',
          'focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50',
          !value && !isFocused && 'empty:before:content-[attr(data-placeholder)] empty:before:text-gray-400',
        )}
        style={{ minHeight }}
        data-placeholder={placeholder}
        suppressContentEditableWarning
      />

      {/* Custom styles for the editor content */}
      <style>{`
        .prose h1 {
          font-size: 2em;
          font-weight: bold;
          margin-top: 0.5em;
          margin-bottom: 0.5em;
        }
        .prose h2 {
          font-size: 1.5em;
          font-weight: bold;
          margin-top: 0.5em;
          margin-bottom: 0.5em;
        }
        .prose h3 {
          font-size: 1.25em;
          font-weight: bold;
          margin-top: 0.5em;
          margin-bottom: 0.5em;
        }
        .prose blockquote {
          border-left: 4px solid #e5e7eb;
          padding-left: 1em;
          margin-left: 0;
          color: #6b7280;
          font-style: italic;
        }
        .prose pre {
          background-color: #f3f4f6;
          padding: 1em;
          border-radius: 0.375rem;
          overflow-x: auto;
          font-family: monospace;
        }
        .prose ul {
          list-style-type: disc;
          margin-left: 1.5em;
          margin-top: 0.5em;
          margin-bottom: 0.5em;
        }
        .prose ol {
          list-style-type: decimal;
          margin-left: 1.5em;
          margin-top: 0.5em;
          margin-bottom: 0.5em;
        }
        .prose li {
          margin-top: 0.25em;
          margin-bottom: 0.25em;
        }
        .prose img {
          max-width: 100%;
          height: auto;
          border-radius: 0.375rem;
          margin-top: 1em;
          margin-bottom: 1em;
        }
        .prose p {
          margin-top: 0.5em;
          margin-bottom: 0.5em;
        }
      `}</style>
    </div>
  )
}
